// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getRemoteConfig, fetchAndActivate, getValue, isSupported, activate } from 'firebase/remote-config';

// Try to import FirebaseError for proper error type checking
let FirebaseError: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const firebaseApp = require('firebase/app');
  FirebaseError = firebaseApp.FirebaseError;
} catch (error) {
  // FirebaseError might not be available in all SDK versions
  console.warn('[Firebase] FirebaseError not available for type checking');
}

// Try to import onConfigUpdate for real-time updates (SDK v12.3.0+)
let onConfigUpdate: any = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { onConfigUpdate: onConfigUpdateFn } = require('firebase/remote-config');
  onConfigUpdate = onConfigUpdateFn;
  console.log('[Firebase] onConfigUpdate imported successfully');
} catch (error) {
  console.warn('[Firebase] onConfigUpdate not available in this SDK version');
}

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: 'AIzaSyCip7jg0PXNokPl6gVruR7PULSm7B3YLoM',
  authDomain: 'thmanyah-platform.firebaseapp.com',
  projectId: 'thmanyah-platform',
  storageBucket: 'thmanyah-platform.firebasestorage.app',
  messagingSenderId: '970693471941',
  appId: '1:970693471941:web:6cb5bc4cd4f3e79a5d0aac',
  measurementId: 'G-LB699N65H4',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// Initialize Remote Config
const remoteConfig = getRemoteConfig(app);

// Set fetch interval based on environment
// Development: 10 seconds for faster testing
// Production: 5 minutes for reasonable real-time updates
// Treat pre-prod environments (WEBAPP pointing to pre-prod) as development-like for faster iteration.
const isPreProd = process.env.VUE_APP_WEBAPP_API_URL?.includes('pre-prod');
const isDevelopment = process.env.NODE_ENV === 'development' || isPreProd;
remoteConfig.settings.minimumFetchIntervalMillis = isDevelopment ? 10000 : 300000; // 10 sec dev, 5 min prod

console.log(
  '[Firebase RemoteConfig] Fetch interval set to:',
  remoteConfig.settings.minimumFetchIntervalMillis / 1000,
  'seconds',
);

// Default values for Remote Config
remoteConfig.defaultConfig = {
  developers: JSON.stringify({
    ids: [],
    testStreams: [
      {
        matchId: '123456',
        channel: 'ch09',
        matchTitle: '_',
        streamType: '_',
        maxBitrate: '_',
        coverKey: '',
        tokenUrl: '',
      },
    ],
  }),
  streams: JSON.stringify({}),
  TV_config: JSON.stringify({
    featureFlags: {
      enable_watching_limit: false,
      moviesSeries: false,
      useVideoWatcher: false,
    },
  }),
};

/**
 * Check if an error is a Firebase quota exceeded error
 * Properly checks Firebase error codes, HTTP status codes, and error messages
 *
 * Firebase Remote Config throws: "Error: Quota exceeded for quota metric 'Read requests'
 * and limit 'Read requests per minute' of service 'firebaseremoteconfig.googleapis.com'..."
 *
 * @param error - The error object to check
 * @returns true if the error indicates quota exceeded
 */
export function isQuotaExceededError(error: any): boolean {
  if (!error) return false;

  // Get error message (most reliable for Firebase Remote Config quota errors)
  const errorMessage = (error.message || error.toString() || '').toLowerCase();

  // Check for the specific Firebase Remote Config quota error message pattern
  // "Quota exceeded for quota metric 'Read requests' and limit 'Read requests per minute'"
  if (
    errorMessage.includes('quota exceeded') ||
    (errorMessage.includes('quota') && errorMessage.includes('exceeded')) ||
    errorMessage.includes('firebaseremoteconfig.googleapis.com') ||
    errorMessage.includes('read requests per minute')
  ) {
    return true;
  }

  // Check if it's a FirebaseError instance (if available)
  if (FirebaseError && error instanceof FirebaseError) {
    // Check Firebase error codes for quota/resource exhaustion
    const code = error.code || '';
    if (
      code === 'resource-exhausted' ||
      code === 'functions/resource-exhausted' ||
      code === 'remote-config/quota-exceeded' ||
      code.toLowerCase().includes('quota') ||
      code.toLowerCase().includes('exceeded')
    ) {
      return true;
    }
  }

  // Check error code property (standard Firebase error structure)
  const errorCode = error.code || '';
  if (
    errorCode === 'resource-exhausted' ||
    errorCode === 'functions/resource-exhausted' ||
    errorCode === 'remote-config/quota-exceeded' ||
    errorCode === '429' ||
    errorCode === 'QUOTA_EXCEEDED' ||
    errorCode.toLowerCase().includes('quota') ||
    errorCode.toLowerCase().includes('exceeded')
  ) {
    return true;
  }

  // Check HTTP status codes
  // 429 = Too Many Requests (rate limit/quota)
  // 500 = Internal Server Error (documentation mentions HTTP 500 for quota errors)
  if (error.response) {
    const status = error.response.status;
    if (status === 429 || status === 500) {
      // For 500, check if message indicates quota (to avoid false positives)
      if (status === 500 && errorMessage.includes('quota')) {
        return true;
      }
      // 429 is always a quota/rate limit error
      if (status === 429) {
        return true;
      }
    }
  }

  // Check HTTP status in error object directly
  if (error.status === 429 || (error.status === 500 && errorMessage.includes('quota'))) {
    return true;
  }

  // Additional fallback checks for other quota-related messages
  if (
    errorMessage.includes('resource-exhausted') ||
    errorMessage.includes('too many requests') ||
    errorMessage.includes('rate limit')
  ) {
    return true;
  }

  return false;
}

/**
 * Safe wrapper for fetchAndActivate that handles quota errors gracefully
 * @returns Promise that resolves to true if successful, false if quota exceeded or other error
 */
export async function safeFetchAndActivate(): Promise<boolean> {
  try {
    await fetchAndActivate(remoteConfig);
    return true;
  } catch (error: any) {
    // Log error details for debugging
    const errorDetails = {
      message: error?.message,
      code: error?.code,
      status: error?.status || error?.response?.status,
      name: error?.name,
      isFirebaseError: FirebaseError ? error instanceof FirebaseError : 'unknown',
    };

    if (isQuotaExceededError(error)) {
      console.error('[Firebase] Quota exceeded error detected. Falling back to default/cached values.');
      console.error('[Firebase] Error details:', errorDetails);
      return false;
    }

    // Log other errors for debugging but don't treat them as quota errors
    console.error('[Firebase] Error fetching remote config (non-quota error):', error);
    console.error('[Firebase] Error details:', errorDetails);
    return false;
  }
}

export { app, analytics, remoteConfig, fetchAndActivate, getValue, isSupported, activate, onConfigUpdate };
